// Firmware (Admin) — every installed device, what it is running, and whether it
// needs updating. Super Admin only: it reflashes hardware.
//
// ── Why this is a page and not another dialog ─────────────────────────────
// The Update Code dialog answers "update this room / this location", and it is
// still the right tool for that. This answers the question that comes first —
// WHICH devices need it, anywhere — and that is a list you sort, filter, search
// and come back to, not something a capped-height dialog can hold.
//
// ⚠️ THE LOAD IS EXPENSIVE AND IS THEREFORE ON DEMAND. One call per location
// plus one per DEVICE — roughly 1400 requests for the estate, six at a time.
// See core/devices/firmwareFleet for the three cheaper routes that were probed
// and are all dead ends. It never runs on mount; the operator presses Load.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, AppBar, Toolbar, IconButton, Button, TextField, MenuItem,
  Alert, Tooltip, Chip, Checkbox, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, LinearProgress, FormControlLabel, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions, useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import StopIcon from '@mui/icons-material/Stop';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MemoryIcon from '@mui/icons-material/Memory';
import UploadBinFileDialog from '../../shared/UploadBinFileDialog';
import DeviceFirmwareDialog from '../../shared/DeviceFirmwareDialog';
import { useApp } from '../../../core/state/AppContext';
import { useFirmwareFleet, STATUS_FILTERS } from '../../../core/hooks/useFirmwareFleet';
import {
  FLEET_LIVE, CURRENT, STALE,
} from '../../../core/devices/firmwareFleet';
import { BUILD_STAMP_TOLERANCE_MS } from '../../../core/devices/firmwareInfo';
import { FIT_SM } from '../../shared/fitDialog';

const STATE_CHIP = {
  [STALE]: { label: 'Needs update', color: 'warning', filled: true },
  [CURRENT]: { label: 'Up to date', color: 'success', filled: false },
  unknown: { label: 'Not known', color: 'default', filled: false },
};

const when = (ms) => (ms == null ? '—'
  : new Date(ms).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));

const ago = (ms) => {
  if (ms == null) return 'never';
  const m = Math.floor(ms / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

export default function FirmwareScreen() {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  const { userId, isSuperAdmin } = useApp();
  const f = useFirmwareFleet(userId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);

  if (!isSuperAdmin) {
    return <Box sx={{ p: 4 }}><Alert severity="warning">This screen is for administrators.</Alert></Box>;
  }

  const loading = f.phase === 'devices' || f.phase === 'firmware';
  const pct = f.progress.total ? (f.progress.done / f.progress.total) * 100 : 0;

  const cell = { py: 0.75 };

  return (
    <Box sx={{ bgcolor: c.back, minHeight: '100%' }}>
      <AppBar position="sticky" elevation={2}>
        <Toolbar variant="dense" sx={{ gap: 1 }}>
          <IconButton edge="start" size="small" color="inherit" onClick={() => navigate(-1)}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: 15, mr: 1 }}>Firmware</Typography>

          {!loading && (
            <Button
              size="small" variant="outlined" color="inherit" onClick={f.load}
              sx={{ textTransform: 'none', borderRadius: 999, py: 0.15 }}
            >
              {f.rows.length ? 'Reload' : 'Load all devices'}
            </Button>
          )}
          {loading && (
            <Button
              size="small" variant="outlined" color="inherit" onClick={f.stop}
              startIcon={<StopIcon />}
              sx={{ textTransform: 'none', borderRadius: 999, py: 0.15 }}
            >
              Stop
            </Button>
          )}

          {/* Publishing is independent of the list — it needs no load, and an
              operator who has just built firmware should not have to pay 1,400
              requests before they can upload it. */}
          <Button
            size="small" variant="outlined" color="inherit"
            onClick={() => setUploadOpen(true)}
            startIcon={<UploadFileIcon />}
            sx={{ textTransform: 'none', borderRadius: 999, py: 0.15 }}
          >
            Publish a build
          </Button>

          {/* By unique number, because the boards that need re-pointing have no
              location and appear in no list — see DeviceFirmwareDialog. */}
          <Button
            size="small" variant="outlined" color="inherit"
            onClick={() => setMapOpen(true)}
            startIcon={<MemoryIcon />}
            sx={{ textTransform: 'none', borderRadius: 999, py: 0.15 }}
          >
            Set a device&apos;s file
          </Button>

          <Box sx={{ flex: 1 }} />

          {/* Liveness and firmware are SEPARATE axes — an offline device can
              still be known to need an update, and hiding it would hide the one
              most likely to have been forgotten. */}
          {f.rows.length > 0 && (
            <>
              <Chip size="small" label={`${f.counts.total} devices`} sx={{ height: 20, fontSize: 11, bgcolor: `${c.text}22`, color: c.text }} />
              <Chip size="small" label={`live ${f.counts.live}`} sx={{ height: 20, fontSize: 11, bgcolor: '#4CAF5033', color: '#A5D6A7' }} />
              <Chip size="small" label={`needs update ${f.counts.needsUpdate}`} sx={{ height: 20, fontSize: 11, bgcolor: '#FFB74D33', color: '#FFCC80' }} />
              <Chip size="small" label={`up to date ${f.counts.upToDate}`} sx={{ height: 20, fontSize: 11, bgcolor: '#4CAF5022', color: '#A5D6A7' }} />
              <Chip size="small" label={`not known ${f.counts.unknown}`} sx={{ height: 20, fontSize: 11, bgcolor: `${c.text}22`, color: c.text }} />
            </>
          )}
        </Toolbar>

        {loading && (
          <Box sx={{ px: 2, pb: 1 }}>
            <LinearProgress variant={f.progress.total ? 'determinate' : 'indeterminate'} value={pct} />
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              {f.phase === 'devices'
                ? `Reading locations — ${f.progress.done}/${f.progress.total} · ${f.progress.label}`
                : `Reading firmware — ${f.progress.done}/${f.progress.total} · ${f.progress.label}`}
            </Typography>
          </Box>
        )}
      </AppBar>

      <Box sx={{ p: 2 }}>
        {f.error && <Alert severity="error" sx={{ mb: 2 }}>{f.error}</Alert>}

        {f.phase === 'idle' && !f.rows.length && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <SystemUpdateAltIcon sx={{ fontSize: 56, color: `${c.text}66` }} />
            <Typography sx={{ color: c.text, fontSize: 18, fontWeight: 700, mt: 2 }}>
              Every device, and what it is running
            </Typography>
            {/* Says the cost BEFORE it is paid. ~1400 requests is a minute of
                waiting, and a button that silently starts that is a button
                people press twice. */}
            <Typography sx={{ color: `${c.text}99`, mt: 1, maxWidth: 560, mx: 'auto', fontSize: 13 }}>
              This reads every location, then every device&apos;s firmware — around
              1,400 requests, six at a time, so it takes a minute or so. Nothing
              is sent to any device until you tick rows and press Update.
            </Typography>
            <Button
              variant="contained" onClick={f.load}
              sx={{ mt: 3, bgcolor: c.button, textTransform: 'none' }}
            >
              Load all devices
            </Button>
            <Box sx={{ mt: 1 }}>
              <Button
                size="small" onClick={() => setUploadOpen(true)}
                startIcon={<UploadFileIcon />}
                sx={{ textTransform: 'none', color: c.text }}
              >
                Or publish a new firmware build
              </Button>
            </Box>
          </Box>
        )}

        {(f.phase === 'sending' || f.phase === 'done') && (
          <Alert severity={f.phase === 'done' ? 'success' : 'info'} sx={{ mb: 2 }}>
            <Typography variant="body2">
              {f.phase === 'done'
                ? `Sent to ${f.sent} device${f.sent === 1 ? '' : 's'}${f.queuedCount ? `, and queued ${f.queuedCount} offline one${f.queuedCount === 1 ? '' : 's'} for their next 20-minute poll` : ''}.`
                  + ' They download and reflash on their own and will drop offline briefly — there is no acknowledgement, so re-load in a minute or two to check.'
                : `Sending… ${f.sent} of ${f.targets.length}`}
            </Typography>
            {f.phase === 'sending' && (
              <LinearProgress
                variant="determinate"
                value={f.targets.length ? (f.sent / f.targets.length) * 100 : 100}
                sx={{ mt: 1 }}
              />
            )}
            {f.phase === 'done' && (
              <Button size="small" onClick={f.backToList} sx={{ mt: 1 }}>Back to the list</Button>
            )}
          </Alert>
        )}

        {f.rows.length > 0 && (
          <>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1.5 }}>
              <TextField
                select size="small" label="Status" value={f.statusFilter}
                onChange={(e) => f.setStatusFilter(e.target.value)}
                sx={{ minWidth: 150, '& .MuiInputBase-input': { color: c.text } }}
              >
                {STATUS_FILTERS.map((s) => (
                  <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                select size="small" label="Location" value={f.locationFilter}
                onChange={(e) => f.setLocationFilter(e.target.value)}
                sx={{ minWidth: 220, '& .MuiInputBase-input': { color: c.text } }}
              >
                <MenuItem value="">Every location</MenuItem>
                {f.locations.map((l) => (
                  <MenuItem key={l.name} value={l.name}>{`${l.name} (${l.count})`}</MenuItem>
                ))}
              </TextField>
              {/* The .bin files actually in use, each with how many devices are
                  on it and how many of those are behind — so picking a build
                  answers "who is on this, and who is stale" in one move. */}
              <TextField
                select size="small" label="Firmware file" value={f.binFilter}
                onChange={(e) => f.setBinFilter(e.target.value)}
                sx={{ minWidth: 300, '& .MuiInputBase-input': { color: c.text } }}
              >
                <MenuItem value="">{`Every file (${f.binFiles.length})`}</MenuItem>
                {f.binFiles.map((b) => (
                  <MenuItem key={b.name} value={b.name}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                      <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5, flex: 1 }}>
                        {b.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {b.builtAt ? new Date(b.builtAt).toLocaleDateString() : '—'}
                      </Typography>
                      <Chip size="small" label={b.count} sx={{ height: 18, fontSize: 10 }} />
                      {b.needsUpdate > 0 && (
                        <Chip
                          size="small" color="warning" label={`${b.needsUpdate} stale`}
                          sx={{ height: 18, fontSize: 10 }}
                        />
                      )}
                    </Box>
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small" placeholder="Search name, number, room…"
                value={f.search} onChange={(e) => f.setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                }}
                sx={{ minWidth: 260, '& .MuiInputBase-input': { color: c.text } }}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={f.liveOnly} onChange={(e) => f.setLiveOnly(e.target.checked)} />}
                label={<Typography variant="caption">Live only</Typography>}
              />
              {/* Makes offline devices selectable. They are reached by the two
                  server-side routes instead of MQTT — both confirmed in the
                  ESP8266 and ESP32-C3 firmware. Still explicit, because ticking
                  it can turn a rollout of four into a rollout of forty. */}
              <Tooltip title="Offline devices cannot be reached over MQTT, but the firmware polls the server every 20 minutes for a parked command, and re-reads its update mark on every Wi-Fi connect. Both are confirmed in the ESP8266 and ESP32-C3 builds.">
                <FormControlLabel
                  control={<Checkbox size="small" checked={f.queueOffline} onChange={(e) => f.setQueueOffline(e.target.checked)} />}
                  label={<Typography variant="caption">Include offline (~20 min)</Typography>}
                />
              </Tooltip>

              <Box sx={{ flex: 1 }} />

              <Button size="small" onClick={f.selectNeeding} sx={{ textTransform: 'none' }}>
                Select all needing
              </Button>
              <Button size="small" onClick={f.clearSelection} disabled={!f.targets.length} sx={{ textTransform: 'none' }}>
                Clear
              </Button>
              <Button
                variant="contained" color="warning" size="small"
                disabled={(!f.targets.length && !f.queued.length) || f.phase === 'sending'}
                onClick={() => setConfirmOpen(true)}
                startIcon={<SystemUpdateAltIcon />}
                sx={{ textTransform: 'none' }}
              >
                {`Update ${f.targets.length}`}
              </Button>
            </Box>

            {/* Counts for what is ON SCREEN. Filtering to one .bin file and
                still reading the estate's "needs update 214" answers a question
                nobody asked. */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              {`Showing ${f.visible.length} of ${f.rows.length}`}
              {f.visible.length !== f.rows.length
                ? ` — ${f.visibleCounts.needsUpdate} need updating, ${f.visibleCounts.live} live`
                : ''}
              {f.queueOffline
                ? '. Offline devices are ticked too: they are queued on the server and pick the command up within about 20 minutes.'
                : '. Offline devices cannot be ticked — a publish they are not listening for is dropped, and they would look updated. Turn on “Queue offline” to reach them.'}
            </Typography>

            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '62vh' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={f.allSelected}
                        indeterminate={f.someSelected && !f.allSelected}
                        disabled={f.selectable.length === 0}
                        onChange={f.toggleAll}
                        inputProps={{ 'aria-label': 'Select all shown' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Number</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Room</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Firmware file</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>OTA file date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Code date</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Live</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {f.visible.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10} sx={{ py: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          Nothing matches these filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  {f.visible.map((r) => {
                    const offline = r.liveState !== FLEET_LIVE;
                    const chip = STATE_CHIP[r.state] || STATE_CHIP.unknown;
                    return (
                      <TableRow
                        key={r.uno}
                        hover={!offline || f.queueOffline}
                        onClick={offline && !f.queueOffline ? undefined : () => f.toggle(r.uno)}
                        sx={{ cursor: offline && !f.queueOffline ? 'default' : 'pointer', opacity: offline ? 0.6 : 1 }}
                      >
                        <TableCell padding="checkbox" sx={cell}>
                          <Checkbox
                            size="small"
                            checked={f.selected.has(r.uno)}
                            disabled={offline && !f.queueOffline}
                            onChange={() => f.toggle(r.uno)}
                            onClick={(e) => e.stopPropagation()}
                            inputProps={{ 'aria-label': `Update ${r.deviceName}` }}
                          />
                        </TableCell>
                        <TableCell sx={cell}>
                          <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                            {r.deviceName}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {r.deviceTypeName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ ...cell, fontFamily: 'monospace', fontSize: 12.5 }}>
                          {r.uno}
                        </TableCell>
                        <TableCell sx={cell}>
                          <Typography variant="body2">{r.locationName}</Typography>
                        </TableCell>
                        <TableCell sx={cell}>
                          <Typography variant="body2">{r.roomName}</Typography>
                        </TableCell>
                        {/* Plain monospace, not a link: this is the one string
                            that must be read character by character. URL on hover. */}
                        <TableCell sx={cell}>
                          {r.binFile ? (
                            <Tooltip title={r.binUrl}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>
                                {r.binFile}
                              </Typography>
                            </Tooltip>
                          ) : <Typography variant="body2" color="text.secondary">—</Typography>}
                        </TableCell>
                        <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{when(r.builtAt)}</Typography>
                        </TableCell>
                        <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                          <Typography variant="body2">{when(r.flashedAt)}</Typography>
                        </TableCell>
                        <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>
                          <Chip
                            size="small"
                            label={offline ? ago(r.age) : 'live'}
                            color={offline ? 'default' : 'success'}
                            variant={offline ? 'outlined' : 'filled'}
                            sx={{ height: 20, fontSize: 11 }}
                          />
                        </TableCell>
                        <TableCell sx={cell}>
                          {/* A row reading "Up to date" beside a flash time
                              minutes OLDER than the build looks like a bug, so
                              say which of the two clocks is responsible. */}
                          <Tooltip
                            title={r.withinTolerance
                              ? `Flashed ${Math.max(1, Math.round((r.builtAt - r.flashedAt) / 60000))}`
                                + ' min before the build was stamped. The upload tool records the'
                                + ' date a few minutes after writing the file, so anything within'
                                + ` ${BUILD_STAMP_TOLERANCE_MS / 60000} minutes counts as the same build.`
                              : ''}
                          >
                            <Chip
                              size="small"
                              label={chip.label}
                              color={chip.color}
                              variant={chip.filled ? 'filled' : 'outlined'}
                              sx={{ height: 20, fontSize: 11 }}
                            />
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Box>

      {/* The last look before a fleet reflashes itself. */}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} {...FIT_SM}>
        <DialogTitle>
          {f.queued.length
            ? `Update ${f.targets.length} now and queue ${f.queued.length}?`
            : `Update ${f.targets.length} device${f.targets.length === 1 ? '' : 's'}?`}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="caption" sx={{ display: 'block' }}>
              Command published to each selected device:
            </Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 700, wordBreak: 'break-all' }}>
              {f.payload}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              They download new firmware and reboot. This cannot be undone.
            </Typography>
          </Alert>
          <Typography variant="caption" color="text.secondary">
            {f.targets.map((r) => `${r.deviceName} (${r.uno})`).join(' · ')}
          </Typography>
          {/* The queued half goes by a different route and on a different
              timescale, so it is listed separately rather than blurred into
              one count. */}
          {f.queued.length > 0 && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <Typography variant="body2">
                {`${f.queued.length} offline device${f.queued.length === 1 ? '' : 's'} will be queued on the server instead`}
                {' — they run the command on their next poll, within about 20 minutes.'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {f.queued.map((r) => `${r.deviceName} (${r.uno})`).join(' · ')}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained" color="warning"
            onClick={() => { setConfirmOpen(false); f.run(); }}
          >
            {`Update ${f.targets.length}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Deliberately does NOT re-load the fleet on success: that is 1,400
          requests, and the operator may well want to publish two builds in a
          row. The success message says to reload when they are ready. */}
      <UploadBinFileDialog open={uploadOpen} onClose={() => setUploadOpen(false)} />
      <DeviceFirmwareDialog open={mapOpen} onClose={() => setMapOpen(false)} />
    </Box>
  );
}
